import {
  Box,
  Typography,
  Alert,
  Grid2 as Grid,
  Chip,
  Paper,
} from "@mui/material";
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

/**
 * 발송 결과 인터페이스
 */
interface SendResults {
  total: number;
  successful: number;
  failed: number;
  errors?: Array<{
    email: string;
    error: string;
  }>;
}

/**
 * SendResultSection Props
 */
interface SendResultSectionProps {
  sendResults: SendResults | null;
}

/**
 * 발송 결과 섹션
 * @description 이메일 발송 결과를 표시하는 컴포넌트입니다.
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-15
 */
export const SendResultSection = ({ sendResults }: SendResultSectionProps) => {
  if (!sendResults) return null;

  return (
    <Paper elevation={0} variant="outlined">
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          📊 Step 3: 발송 결과
        </Typography>

        <Grid container spacing={2} mb={2}>
          <Grid>
            <Chip
              icon={<SuccessIcon />}
              label={`성공: ${sendResults.successful}명`}
              color="success"
              variant="outlined"
            />
          </Grid>
          <Grid>
            <Chip
              icon={<ErrorIcon />}
              label={`실패: ${sendResults.failed}명`}
              color="error"
              variant="outlined"
            />
          </Grid>
          <Grid>
            <Chip
              label={`전체: ${sendResults.total}명`}
              color="info"
              variant="outlined"
            />
          </Grid>
        </Grid>

        {sendResults.errors && sendResults.errors.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="bold" mb={1}>
              발송 실패 상세:
            </Typography>
            {sendResults.errors.slice(0, 5).map((error, index) => (
              <Typography key={index} variant="caption" display="block">
                • {error.email}: {error.error}
              </Typography>
            ))}
            {sendResults.errors.length > 5 && (
              <Typography variant="caption" color="text.secondary">
                ... 외 {sendResults.errors.length - 5}건
              </Typography>
            )}
          </Alert>
        )}

        {/* 성공률 표시 */}
        {sendResults.total > 0 && (
          <Alert
            severity={
              sendResults.successful === sendResults.total
                ? "success"
                : "warning"
            }
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" fontWeight="bold">
              📈 발송 성공률:{" "}
              {((sendResults.successful / sendResults.total) * 100).toFixed(1)}%
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {sendResults.successful}명 성공 / {sendResults.total}명 중
            </Typography>
          </Alert>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            💡 <strong>발송 완료 안내:</strong>
            <br />
            • 성공적으로 발송된 고객들은 tmember 테이블에 등록되었습니다.
            <br />
            • 임시패스워드는 SendGrid를 통해 이메일로 발송되었습니다.
            <br />
            • 고객들은 받은 임시패스워드로 로그인 후 비밀번호를 변경해야 합니다.
            <br />• 임시패스워드는 30일 후 만료됩니다.
          </Typography>
        </Alert>
      </Box>
    </Paper>
  );
};
